// PWA Installation Test and Debug Utilities

export const testPWAInstallation = () => {
  console.log('=== PWA Installation Test ===');
  
  const hasServiceWorker = 'serviceWorker' in navigator;
  const hasPushManager = 'PushManager' in window;
  const hasIndexedDB = 'indexedDB' in window;
  const hasLocalStorage = 'localStorage' in window;
  const hasManifest = document.querySelector('link[rel="manifest"]') !== null;
  const hasHTTPS = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
  
  console.log('Basic Requirements:', {
    hasServiceWorker,
    hasPushManager,
    hasIndexedDB,
    hasLocalStorage,
    hasManifest,
    hasHTTPS
  });
  
  // Check manifest
  if (hasManifest) {
    const manifestLink = document.querySelector('link[rel="manifest"]') as HTMLLinkElement;
    console.log('Manifest URL:', manifestLink.href);
    
    fetch(manifestLink.href)
      .then(response => response.json())
      .then(manifest => {
        console.log('Manifest Content:', manifest);
        console.log('Manifest Icons:', manifest.icons?.length || 0);
        console.log('Manifest Display Mode:', manifest.display);
        console.log('Manifest Start URL:', manifest.start_url);
      })
      .catch(error => console.error('Error fetching manifest:', error));
  }
  
  // Check service worker
  if (hasServiceWorker) {
    navigator.serviceWorker.getRegistrations().then(registrations => {
      console.log('Service Worker Registrations:', registrations.length);
      registrations.forEach((reg, index) => {
        console.log(`SW ${index}:`, {
          scope: reg.scope,
          state: reg.active?.state,
          scriptURL: reg.active?.scriptURL
        });
      });
    });
  }
  
  // Check install prompt availability
  console.log('Install Prompt Status:', {
    hasDeferredPrompt: !!(window as any).deferredPrompt,
    isStandalone: window.matchMedia('(display-mode: standalone)').matches,
    isNavigatorStandalone: (window.navigator as any).standalone === true
  });
  
  // Check user engagement
  console.log('User Engagement:', {
    hasUserGesture: (window as any).userGesture || false,
    pageVisibility: document.visibilityState,
    hasFocus: document.hasFocus()
  });
  
  // Check browser support
  const userAgent = navigator.userAgent;
  const isChrome = /Chrome/.test(userAgent) && !/(FxiOS|OPR|Edg|Edge|EdgA|EdgiOS)/i.test(userAgent);
  const isFirefox = /Firefox|FxiOS/.test(userAgent);
  const isSafari = /^((?!Chrome|Android).)*Safari/i.test(userAgent);
  const isEdge = /Edg|Edge|EdgA|EdgiOS/i.test(userAgent);
  
  console.log('Browser Support:', {
    isChrome,
    isFirefox,
    isSafari,
    isEdge,
    userAgent
  });
  
  // Mobile-specific checks
  const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const hasViewportMeta = document.querySelector('meta[name="viewport"]') !== null;
  
  console.log('Mobile Checks:', {
    isMobile,
    isTouchDevice,
    hasViewportMeta,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight
  });
  
  // Overall assessment
  const canInstall = hasServiceWorker && hasManifest && hasHTTPS && (isChrome || isFirefox || isEdge);
  console.log('Can Install PWA:', canInstall);
  
  return {
    canInstall,
    hasServiceWorker,
    hasManifest,
    hasHTTPS,
    isSupportedBrowser: isChrome || isFirefox || isEdge,
    isMobile,
    isTouchDevice
  };
};

export const testVersionInfo = () => {
  console.log('=== Version Information Test ===');
  
  const versionInfo = {
    appVersion: (window as any).APP_VERSION || 'Not set',
    appBuild: (window as any).APP_BUILD || 'Not set',
    appTimestamp: (window as any).APP_TIMESTAMP || 'Not set',
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    cookieEnabled: navigator.cookieEnabled,
    onLine: navigator.onLine
  };
  
  console.log('Version Info:', versionInfo);
  
  // Try to fetch version.json
  fetch('/version.json')
    .then(response => response.json())
    .then(data => {
      console.log('Version.json data:', data);
    })
    .catch(error => {
      console.log('Could not fetch version.json:', error);
    });
  
  return versionInfo;
};

// Enhanced PWA installation trigger
export const triggerPWAInstall = async () => {
  console.log('=== Triggering PWA Install ===');
  
  // Check if already installed
  if (window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true) {
    console.log('PWA is already installed');
    return { success: false, reason: 'Already installed' };
  }
  
  // Check for deferred prompt
  if ((window as any).deferredPrompt) {
    console.log('Using deferred prompt');
    try {
      await (window as any).deferredPrompt.prompt();
      const { outcome } = await (window as any).deferredPrompt.userChoice;
      console.log('Install prompt outcome:', outcome);
      return { success: outcome === 'accepted', reason: outcome };
    } catch (error) {
      console.error('Error with deferred prompt:', error);
      return { success: false, reason: 'Deferred prompt error' };
    }
  }
  
  // Try to trigger beforeinstallprompt event
  console.log('Attempting to trigger beforeinstallprompt event');
  const event = new Event('beforeinstallprompt');
  window.dispatchEvent(event);
  
  // Wait a bit and check if prompt became available
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  if ((window as any).deferredPrompt) {
    console.log('Deferred prompt now available, trying again');
    try {
      await (window as any).deferredPrompt.prompt();
      const { outcome } = await (window as any).deferredPrompt.userChoice;
      return { success: outcome === 'accepted', reason: outcome };
    } catch (error) {
      console.error('Error with triggered prompt:', error);
      return { success: false, reason: 'Triggered prompt error' };
    }
  }
  
  console.log('No install prompt available');
  return { success: false, reason: 'No install prompt available' };
};

// Make functions available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).testPWAInstallation = testPWAInstallation;
  (window as any).testVersionInfo = testVersionInfo;
  (window as any).triggerPWAInstall = triggerPWAInstall;
}